import { useEffect, useMemo, useRef, useState } from 'react'
import { useAnimations } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js'
import { MODELS } from '../../config'

// tiny module cache: remounts never refetch, failed loads can retry cleanly
const gltfCache = new Map()

/** Kick off all model downloads immediately at app parse time. */
export function prefetchModels() {
  ;[MODELS.idle, MODELS.landing, '/models/speaking_avatar_readyplayerme.glb'].forEach((url) => {
    if (gltfCache.has(url)) return
    new GLTFLoader().load(url, (g) => gltfCache.set(url, g))
  })
}

/**
 * Suspense-free GLTF loading with automatic retries — a failed or stalled
 * model fetch can never leave a canvas permanently empty.
 * Reports readiness via onReady so parents can run camera fits AFTER the
 * model actually exists.
 */
export function useGLTFScene(url, { maxAttempts = 3, onReady, onMissing } = {}) {
  const [gltf, setGltf] = useState(() => gltfCache.get(url) || null)
  const [attempt, setAttempt] = useState(0)
  const reportedRef = useRef(false)

  useEffect(() => {
    if (gltfCache.has(url)) {
      setGltf(gltfCache.get(url))
      return
    }
    let alive = true
    let stallTimer
    const clearStall = () => clearTimeout(stallTimer)
    const loader = new GLTFLoader()
    // failsafe: a loader that never calls back (stalled connection) counts
    // as a failure so the retry chain / onMissing always runs
    stallTimer = setTimeout(() => {
      if (alive && attempt < maxAttempts - 1) {
        alive = false
        setAttempt((a) => a + 1)
      } else if (alive) {
        alive = false
        onMissing?.()
      }
    }, 10000)
    loader.load(
      url,
      (g) => {
        clearStall()
        gltfCache.set(url, g)
        if (alive) setGltf(g)
      },
      undefined,
      () => {
        clearStall()
        if (alive && attempt < maxAttempts - 1) {
          setTimeout(() => alive && setAttempt((a) => a + 1), 1200)
        } else if (alive) {
          onMissing?.()
        }
      }
    )
    return () => {
      alive = false
      clearStall()
    }
  }, [url, attempt, maxAttempts, onMissing])

  useEffect(() => {
    if (gltf && !reportedRef.current) {
      reportedRef.current = true
      onReady?.(gltf)
    }
  }, [gltf, onReady])

  return gltf
}

// Avaturn hair/glass alpha-sort fix + frustum pop fix (see asset README)
function fixMaterials(scene) {
  scene.traverse((child) => {
    if (!child.isMesh) return
    child.frustumCulled = false
    if (child.material) {
      if (
        child.material.transparent ||
        child.material.alphaMap ||
        child.name.toLowerCase().includes('hair')
      ) {
        child.material.transparent = true
        child.material.depthWrite = true
        child.material.alphaTest = 0.5
        child.material.side = THREE.DoubleSide
      }
    }
  })
}

/**
 * The one true Vedant (hero). Both clips — superhero landing + idle
 * breathing — live on this single rigged character (same Avaturn skeleton).
 * Renders nothing until its own scene instance is ready, then calls onReady
 * so the parent can mount the camera fit.
 */
export default function CharacterModel({ withLanding = false, headTracking = true, onReady, onMissing, ...props }) {
  const group = useRef()
  const idleGltf = useGLTFScene(MODELS.idle, { onMissing })
  const landingGltf = useGLTFScene(withLanding ? MODELS.landing : MODELS.idle)

  // confident entry: render NOTHING until the idle model AND (for the hero)
  // the landing clip are both in hand — a late-arriving landing clip must
  // never yank a standing character back up into the sky
  const clipsReady = !!idleGltf && (!withLanding || !!landingGltf)
  const reportedRef = useRef(false)

  useEffect(() => {
    if (clipsReady && !reportedRef.current) {
      reportedRef.current = true
      onReady?.()
    }
  }, [clipsReady, onReady])

  const scene = useMemo(() => {
    if (!idleGltf) return null
    const s = skeletonClone(idleGltf.scene)
    fixMaterials(s)
    return s
  }, [idleGltf])

  const clips = useMemo(() => {
    if (!idleGltf) return []
    const list = idleGltf.animations.map((a) => {
      const c = a.clone()
      c.name = 'Idle'
      return c
    })
    if (withLanding && landingGltf) {
      landingGltf.animations.forEach((a) => {
        const c = a.clone()
        c.name = 'Landing'
        list.push(c)
      })
    }
    return list
  }, [idleGltf, landingGltf, withLanding])

  const { actions } = useAnimations(clips, group)

  // clipKey: when the landing clip arrives late, the entrance effect re-runs
  const clipKey = clips.map((c) => c.name).join('|')

  useEffect(() => {
    if (!clipsReady || !scene || !actions['Idle']) return
    let timer
    if (withLanding && actions['Landing']) {
      const landing = actions['Landing']
      landing.reset()
      landing.setLoop(THREE.LoopOnce, 1)
      landing.clampWhenFinished = true
      landing.play()
      const duration = landing.getClip().duration
      timer = setTimeout(() => {
        actions['Idle'].reset().play()
        actions['Idle'].crossFadeFrom(landing, 0.9, true)
      }, duration * 1000 + 600)
    } else {
      actions['Idle'].reset().fadeIn(0.4).play()
    }
    return () => clearTimeout(timer)
  }, [actions, withLanding, clipKey, scene, clipsReady])

  const headBone = useMemo(() => {
    if (!scene) return null
    let bone = null
    scene.traverse((child) => {
      if (!bone && child.isBone && child.name.toLowerCase().includes('head')) bone = child
    })
    return bone
  }, [scene])

  useFrame((state) => {
    if (!headTracking || !headBone) return
    const targetX = (state.mouse.x * Math.PI) / 2.2
    const targetY = (state.mouse.y * Math.PI) / 3
    headBone.rotation.y += (targetX - headBone.rotation.y) * 0.08
    headBone.rotation.x += (-targetY - headBone.rotation.x) * 0.08
  })

  return (
    <group ref={group} {...props}>
      {clipsReady && scene && <primitive object={scene} />}
    </group>
  )
}
